import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}

export function formatRelativeDate(date: string | Date): string {
  const d = dayjs(date);

  if (d.isToday()) {
    return "Today";
  }

  if (d.isYesterday()) {
    return "Yesterday";
  }

  const diffDays = dayjs().diff(d, "day");
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks}w ago`;
  }

  return d.format("MMM D");
}

export function formatFullDate(date: string | Date): string {
  return dayjs(date).format("MMM D, YYYY [at] h:mm A");
}

export function formatDateOnly(date: string | Date): string {
  return dayjs(date).format("MMM D, YYYY");
}

export function formatTimeOnly(date: string | Date): string {
  return dayjs(date).format("h:mm A");
}

export function getRelativeTimeString(date: string | Date): string {
  return dayjs(date).fromNow();
}

export function isExpired(date: string | Date): boolean {
  return dayjs(date).isBefore(dayjs());
}

export function truncateFileName(fileName: string, maxLength: number = 30): string {
  if (fileName.length <= maxLength) return fileName;

  const extension = fileName.includes(".")
    ? "." + fileName.split(".").pop()
    : "";
  const nameWithoutExt = fileName.slice(0, fileName.length - extension.length);
  const availableLength = maxLength - extension.length - 3;

  if (availableLength <= 0) {
    return fileName.slice(0, maxLength - 3) + "...";
  }

  return nameWithoutExt.slice(0, availableLength) + "..." + extension;
}

export function truncateText(text: string, maxLength: number = 50): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}

/**
 * Calculate days until a document is auto-deleted
 */
export function getDaysUntilDeletion(scheduledDeletionAt: string | Date): number {
  const deletionDate = dayjs(scheduledDeletionAt);
  const now = dayjs();
  return Math.max(0, deletionDate.diff(now, "day"));
}

/**
 * Format the deletion countdown text
 */
export function formatDeletionCountdown(scheduledDeletionAt: string | Date): string {
  const days = getDaysUntilDeletion(scheduledDeletionAt);

  if (days === 0) {
    return "Expires today";
  } else if (days === 1) {
    return "Expires tomorrow";
  } else if (days <= 7) {
    return `Expires in ${days} days`;
  } else {
    return `Expires ${dayjs(scheduledDeletionAt).format("MMM D")}`;
  }
}

/**
 * Get urgency level based on days until deletion
 * Used for color coding the countdown indicator
 */
export function getDeletionUrgency(scheduledDeletionAt: string | Date): "critical" | "warning" | "normal" {
  const days = getDaysUntilDeletion(scheduledDeletionAt);

  if (days <= 3) {
    return "critical";
  } else if (days <= 7) {
    return "warning";
  }
  return "normal";
}
